/**
 * Mirrors backend `searchableTextBuilder` for local DynamoDB dev paths.
 * Keep behavior in sync when changing search indexing rules.
 */

export const MAX_SEARCHABLE_TEXT_BYTES = 1024;
export const MAX_SEARCHABLE_TEXT_CHARS = 350_000;

function truncateStringToUtf8Bytes(s: string, maxBytes: number): string {
  if (maxBytes <= 0) return '';
  const enc = new TextEncoder();
  if (enc.encode(s).length <= maxBytes) return s;
  let low = 0;
  let high = s.length;
  while (low < high) {
    const mid = Math.floor((low + high + 1) / 2);
    if (enc.encode(s.slice(0, mid)).length <= maxBytes) low = mid;
    else high = mid - 1;
  }
  return s.slice(0, low);
}
const MAX_OCR_JOIN_CHARS = 280_000;

function pushStringParts(parts: string[], s: unknown): void {
  if (typeof s !== 'string') return;
  const t = s.trim();
  if (t) parts.push(t);
}

export function flattenExtractedDataForSearch(extractedData: unknown): string {
  if (!extractedData || typeof extractedData !== 'object') {
    return '';
  }
  const ed = extractedData as Record<string, unknown>;
  const parts: string[] = [];

  pushStringParts(parts, ed.text);

  const formFields = ed.formFields;
  if (Array.isArray(formFields)) {
    for (const f of formFields) {
      if (!f || typeof f !== 'object') continue;
      const row = f as Record<string, unknown>;
      pushStringParts(parts, row.key);
      pushStringParts(parts, row.value);
    }
  }

  const tables = ed.tables;
  if (Array.isArray(tables)) {
    for (const table of tables) {
      if (!table || typeof table !== 'object') continue;
      const rows = (table as Record<string, unknown>).rows;
      if (!Array.isArray(rows)) continue;
      for (const row of rows) {
        if (!row || typeof row !== 'object') continue;
        const cells = (row as Record<string, unknown>).cells;
        if (!Array.isArray(cells)) continue;
        for (const cell of cells) {
          if (cell != null && typeof cell === 'object') {
            pushStringParts(parts, (cell as Record<string, unknown>).text);
          }
        }
      }
    }
  }

  const joined = parts.join(' ').replace(/\s+/g, ' ').trim();
  if (joined.length <= MAX_OCR_JOIN_CHARS) {
    return joined;
  }
  return joined.slice(0, MAX_OCR_JOIN_CHARS);
}

export interface BuildSearchableTextInput {
  fileName?: string | null;
  storedFields?: Record<string, unknown> | null;
  customFields?: Record<string, unknown> | Array<{ name?: string; value?: string }> | null;
  /** Document.extractedData / Textract payload shape */
  extractedData?: unknown;
}

export function buildDocumentSearchableText(input: BuildSearchableTextInput): string {
  const { fileName, storedFields, customFields, extractedData } = input;

  const segments: string[] = [];

  const fn = (fileName || '').trim();
  if (fn) {
    segments.push(fn.toLowerCase());
  }

  if (storedFields && typeof storedFields === 'object') {
    for (const value of Object.values(storedFields)) {
      if (value && typeof value === 'string' && value.trim()) {
        segments.push(value.toLowerCase());
      }
    }
  }

  if (Array.isArray(customFields)) {
    for (const field of customFields) {
      if (field && typeof field === 'object' && field.value && typeof field.value === 'string' && field.value.trim()) {
        segments.push(field.value.toLowerCase());
      }
    }
  } else if (customFields && typeof customFields === 'object') {
    for (const value of Object.values(customFields)) {
      if (value && typeof value === 'string' && value.trim().length > 0) {
        segments.push(value.toLowerCase());
      } else if (value && typeof value === 'object' && (value as { value?: string }).value) {
        const v = String((value as { value?: string }).value).trim();
        if (v.length > 0) {
          segments.push(v.toLowerCase());
        }
      }
    }
  }

  const ocrFlat = flattenExtractedDataForSearch(extractedData);
  if (ocrFlat) {
    segments.push(ocrFlat.toLowerCase());
  }

  let result = segments.join(' ').replace(/\s+/g, ' ').trim();
  if (result.length > MAX_SEARCHABLE_TEXT_CHARS) {
    result = result.slice(0, MAX_SEARCHABLE_TEXT_CHARS);
  }

  result = truncateStringToUtf8Bytes(result, MAX_SEARCHABLE_TEXT_BYTES);

  if (result.length > 0) {
    return result;
  }
  const fallback = (fn || 'document').toLowerCase() || 'document';
  return truncateStringToUtf8Bytes(fallback, MAX_SEARCHABLE_TEXT_BYTES);
}
